import { AlertTriangle, Download, ExternalLink } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import {
  HubspotConflictItem,
  HubspotConflictReason,
  HubspotCoverageItem,
  HubspotTenantSyncResult
} from '@/api/organization-hubspot-client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Icons } from '@/components/ui/icons'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { toast } from '@/components/ui/use-toast'
import { RoutePaths } from '@/routes/routes'
import {
  useMutationSyncOrganizationHubspotTenants,
  useQueryGetOrganizationHubspotCoverage
} from '@/services/hubspot.service'

const CONFLICT_REASON_LABELS: Record<HubspotConflictReason, string> = {
  linked_company_claims_other_org: 'Linked company points at another org',
  other_company_claims_this_org: 'Another company claims this org',
  multiple_companies_claim_this_org: 'Multiple companies claim this org',
  company_shared_across_org_families: 'Company shared with an unrelated org'
}

const csvEscape = (value: unknown) => {
  const text = value === null || value === undefined ? '' : String(value)

  return `"${text.replace(/"/g, '""')}"`
}

const downloadCsv = (filename: string, rows: HubspotCoverageItem[]) => {
  const headers = [
    'org_id',
    'name',
    'organization_use',
    'parent_production_org_id',
    'hubspot_company_id',
    'inherited_parent_hubspot_company_id',
    'exact_match_company_id',
    'exact_match_company_name',
    'exact_match_domain'
  ]
  const csv = [
    headers.join(','),
    ...rows.map((row) =>
      [
        row.org_id,
        row.name,
        row.organization_use,
        row.parent_production_org_id,
        row.hubspot_company_id,
        row.inherited_parent_hubspot_company_id,
        row.exact_hubspot_match?.company_id,
        row.exact_hubspot_match?.company_name,
        row.exact_hubspot_match?.domain
      ]
        .map(csvEscape)
        .join(',')
    )
  ].join('\n')

  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
  const link = document.createElement('a')

  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

const downloadConflictsCsv = (rows: HubspotConflictItem[]) => {
  const headers = [
    'org_id',
    'name',
    'organization_use',
    'parent_production_org_id',
    'hubspot_company_id',
    'linked_company_name',
    'linked_company_claims_org_id',
    'claiming_company_ids',
    'claiming_company_names',
    'shared_with_org_ids',
    'reasons'
  ]
  const csv = [
    headers.join(','),
    ...rows.map((row) =>
      [
        row.org_id,
        row.name,
        row.organization_use,
        row.parent_production_org_id,
        row.hubspot_company_id,
        row.linked_company?.company_name,
        row.linked_company?.organization_id,
        row.claiming_companies.map((company) => company.company_id).join(' | '),
        row.claiming_companies
          .map((company) => company.company_name)
          .join(' | '),
        row.shared_with_org_ids.join(' | '),
        row.reasons.join(' | ')
      ]
        .map(csvEscape)
        .join(',')
    )
  ].join('\n')

  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
  const link = document.createElement('a')

  link.href = url
  link.download = 'hubspot-mapping-conflicts.csv'
  link.click()
  URL.revokeObjectURL(url)
}

const SummaryTile = ({ label, value }: { label: string; value: number }) => (
  <Card className="bg-background">
    <CardHeader className="p-4 pb-1">
      <CardTitle className="text-sm text-muted-foreground">{label}</CardTitle>
    </CardHeader>
    <CardContent className="p-4 pt-0 text-xl font-semibold">
      {value}
    </CardContent>
  </Card>
)

const CoverageTable = ({
  title,
  rows,
  filename
}: {
  title: string
  rows: HubspotCoverageItem[]
  filename: string
}) => (
  <Card className="bg-background">
    <CardHeader className="flex-row items-center justify-between space-y-0">
      <CardTitle className="text-lg">{title}</CardTitle>
      <Button
        disabled={!rows.length}
        onClick={() => downloadCsv(filename, rows)}
        size="sm"
        variant="outline"
      >
        <Download className="mr-2 h-4 w-4" />
        Export
      </Button>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Organization</TableHead>
            <TableHead>Use</TableHead>
            <TableHead>Parent</TableHead>
            <TableHead>HubSpot</TableHead>
            <TableHead>Suggested match</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length ? (
            rows.slice(0, 100).map((row) => (
              <TableRow key={row.org_id}>
                <TableCell>
                  <Link
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                    to={`${RoutePaths.ORGANIZATIONS}/${row.org_id}`}
                  >
                    {row.name || row.org_id}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                  <div className="text-xs text-muted-foreground">
                    {row.org_id}
                  </div>
                </TableCell>
                <TableCell>{row.organization_use}</TableCell>
                <TableCell>{row.parent_production_org_id || '-'}</TableCell>
                <TableCell>
                  {row.hubspot_company_id ||
                    row.inherited_parent_hubspot_company_id ||
                    '-'}
                </TableCell>
                <TableCell>
                  {row.exact_hubspot_match?.company_id ? (
                    <>
                      <div>{row.exact_hubspot_match.company_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {row.exact_hubspot_match.company_id}
                        {row.exact_hubspot_match.domain
                          ? ` · ${row.exact_hubspot_match.domain}`
                          : ''}
                      </div>
                    </>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                className="py-8 text-center text-muted-foreground"
                colSpan={5}
              >
                No organizations in this group.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {rows.length > 100 && (
        <div className="pt-3 text-xs text-muted-foreground">
          Showing first 100 rows. Export CSV for the full list.
        </div>
      )}
    </CardContent>
  </Card>
)

const ConflictsTable = ({ rows }: { rows: HubspotConflictItem[] }) => (
  <Card className="bg-background">
    <CardHeader className="flex-row items-center justify-between space-y-0">
      <div>
        <CardTitle className="flex items-center gap-2 text-lg">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          Mapping conflicts
        </CardTitle>
        <p className="pt-1 text-sm text-muted-foreground">
          Organizations whose HubSpot company link disagrees with what HubSpot
          points back at. Review before syncing.
        </p>
      </div>
      <Button
        disabled={!rows.length}
        onClick={() => downloadConflictsCsv(rows)}
        size="sm"
        variant="outline"
      >
        <Download className="mr-2 h-4 w-4" />
        Export
      </Button>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Organization</TableHead>
            <TableHead>Use</TableHead>
            <TableHead>Linked company</TableHead>
            <TableHead>HubSpot points at</TableHead>
            <TableHead>Problem</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length ? (
            rows.map((row) => (
              <TableRow key={row.org_id}>
                <TableCell>
                  <Link
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                    to={`${RoutePaths.ORGANIZATIONS}/${row.org_id}`}
                  >
                    {row.name || row.org_id}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                  <div className="text-xs text-muted-foreground">
                    {row.org_id}
                  </div>
                </TableCell>
                <TableCell>{row.organization_use}</TableCell>
                <TableCell>
                  <div>{row.linked_company?.company_name || '-'}</div>
                  <div className="text-xs text-muted-foreground">
                    {row.hubspot_company_id}
                  </div>
                </TableCell>
                <TableCell>
                  {row.linked_company?.organization_id ? (
                    <div>org {row.linked_company.organization_id}</div>
                  ) : (
                    <div className="text-muted-foreground">no org property</div>
                  )}
                  {row.claiming_companies.length ? (
                    <div className="text-xs text-muted-foreground">
                      claimed by{' '}
                      {row.claiming_companies
                        .map(
                          (company) =>
                            `${company.company_name || 'unnamed'} (${
                              company.company_id
                            })`
                        )
                        .join(', ')}
                    </div>
                  ) : null}
                  {row.shared_with_org_ids.length ? (
                    <div className="text-xs text-muted-foreground">
                      also linked from org {row.shared_with_org_ids.join(', ')}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {row.reasons.map((reason) => (
                      <Badge className="w-fit" key={reason} variant="outline">
                        {CONFLICT_REASON_LABELS[reason] || reason}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                className="py-8 text-center text-muted-foreground"
                colSpan={5}
              >
                No conflicts detected.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
)

/**
 * Pushes Tenant records into HubSpot for production organizations and their
 * sandboxes. Preview is required before applying, because this writes to the CRM.
 */
const TenantSyncPanel = ({
  conflictOrgIds
}: {
  conflictOrgIds: Set<string>
}) => {
  const [preview, setPreview] = useState<HubspotTenantSyncResult | null>(null)
  const [scope, setScope] = useState('')
  const { mutateAsync, isPending } = useMutationSyncOrganizationHubspotTenants()

  const scopedOrgIds = scope
    .split(/[\s,]+/)
    .map((value) => value.trim())
    .filter(Boolean)

  const runSync = async (dryRun: boolean) => {
    try {
      const result = await mutateAsync({
        dryRun,
        orgIds: scopedOrgIds.length ? scopedOrgIds : undefined
      })

      setPreview(dryRun ? result : null)

      toast({
        title: dryRun ? 'Preview ready' : 'Tenants synced to HubSpot',
        description: `${result.summary.created} to create, ${result.summary.updated} to update, ${result.summary.associations_to_add} associations to add, ${result.summary.stale_associations_to_remove} stale to remove, ${result.summary.skipped_no_company} skipped without a company.`
      })
    } catch (error) {
      toast({
        title: 'HubSpot tenant sync failed',
        description:
          error instanceof Error
            ? error.message
            : 'Check that HubSpot write-back is configured.',
        variant: 'destructive'
      })
    }
  }

  // Any record with work to do counts — a Tenant whose fields already match but
  // whose company association is wrong is exactly the case worth applying.
  const pendingWrites = preview?.summary.pending ?? 0
  const scopedConflicts = scopedOrgIds.filter((orgId) =>
    conflictOrgIds.has(orgId)
  )
  const canApply =
    !isPending &&
    Boolean(preview) &&
    pendingWrites > 0 &&
    scopedOrgIds.length > 0 &&
    scopedConflicts.length === 0

  return (
    <Card className="bg-background">
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-lg">HubSpot Tenant sync</CardTitle>
          <p className="pt-1 text-sm text-muted-foreground">
            Creates or updates a Tenant record per organization - production and
            sandbox - associates it with the HubSpot company, and stamps a link
            back to Admin Portal. Sandboxes inherit their parent production
            organization&apos;s company.
          </p>
        </div>
        <div className="flex shrink-0 items-start gap-2">
          <div>
            <Input
              className="w-64"
              onChange={(event) => setScope(event.target.value)}
              placeholder="Org IDs to apply, comma-separated"
              value={scope}
            />
            <div className="pt-1 text-xs text-muted-foreground">
              {scopedOrgIds.length
                ? `${scopedOrgIds.length} organization(s) scoped`
                : 'Preview runs on all; applying needs an explicit scope'}
            </div>
          </div>
          <Button
            disabled={isPending}
            onClick={() => runSync(true)}
            size="sm"
            variant="outline"
          >
            Preview
          </Button>
          <Button
            disabled={!canApply}
            onClick={() => {
              const staleCount = preview?.summary.stale_associations_to_remove
              const detachNote = staleCount
                ? `\n\nThis also DETACHES ${staleCount} existing company association(s).`
                : ''

              if (
                window.confirm(
                  `Apply ${pendingWrites} Tenant change(s) for ${scopedOrgIds.length} organization(s) to HubSpot? This updates the live CRM.${detachNote}`
                )
              ) {
                runSync(false)
              }
            }}
            size="sm"
          >
            Apply {pendingWrites || ''}
          </Button>
        </div>
      </CardHeader>
      {scopedConflicts.length ? (
        <CardContent className="pb-0">
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
            <div className="flex items-center gap-2 font-medium">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              Apply blocked: {scopedConflicts.join(', ')} appear in mapping
              conflicts
            </div>
            <div className="pt-1 text-muted-foreground">
              Resolve the conflict first — syncing would push the disputed
              company onto the tenant and its sandboxes. Note that conflicts
              only catch disagreements, not a mapping that is consistent but
              points at the wrong company.
            </div>
          </div>
        </CardContent>
      ) : null}
      {preview && (
        <CardContent>
          {preview.unavailable_properties?.length ? (
            <div className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Not synced: these properties do not exist on the HubSpot Tenant
                object
              </div>
              <div className="pt-1 text-muted-foreground">
                {preview.unavailable_properties.join(', ')} — they need creating
                in HubSpot first. Everything else syncs normally.
              </div>
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-4 pb-4 lg:grid-cols-5">
            <SummaryTile
              label="Considered"
              value={preview.summary.considered}
            />
            <SummaryTile label="To create" value={preview.summary.created} />
            <SummaryTile label="To update" value={preview.summary.updated} />
            <SummaryTile label="Unchanged" value={preview.summary.unchanged} />
            <SummaryTile
              label="No company"
              value={preview.summary.skipped_no_company}
            />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Use</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Operations</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {preview.actions.slice(0, 100).map((action) => (
                <TableRow key={action.org_id}>
                  <TableCell>
                    {action.name || action.org_id}
                    <div className="text-xs text-muted-foreground">
                      {action.org_id}
                    </div>
                  </TableCell>
                  <TableCell>{action.organization_use}</TableCell>
                  <TableCell>
                    {action.company_id}
                    {action.company_id_source === 'parent_production_org' && (
                      <div className="text-xs text-muted-foreground">
                        from parent
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {action.operations.join(', ')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {preview.actions.length > 100 && (
            <div className="pt-3 text-xs text-muted-foreground">
              Showing first 100 of {preview.actions.length} planned actions.
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}

const OrganizationHubspotCoverage = () => {
  const { data, isLoading, error } = useQueryGetOrganizationHubspotCoverage()
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Icons.spinner className="h-5 w-5" />
        <span className="text-sm text-muted-foreground">
          Loading HubSpot coverage...
        </span>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="text-sm text-destructive">
        Could not load HubSpot coverage.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-primary">
            HubSpot Coverage
          </h1>
          <p className="text-sm text-muted-foreground">
            Organization links stored in Admin Portal.
          </p>
        </div>
        <Button
          onClick={() => navigate(RoutePaths.ORGANIZATIONS)}
          variant="outline"
        >
          Organizations
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <SummaryTile label="Total" value={data.summary.total} />
        <SummaryTile label="Linked" value={data.summary.linked} />
        <SummaryTile label="Unlinked" value={data.summary.unlinked} />
        <SummaryTile
          label="Exact matches"
          value={data.summary.exact_unlinked_matches}
        />
        <SummaryTile
          label="Sandbox inherits"
          value={data.summary.sandbox_can_inherit_from_parent}
        />
        <SummaryTile label="Conflicts" value={data.summary.conflicts} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SummaryTile
          label="Production linked"
          value={data.summary.production_linked}
        />
        <SummaryTile
          label="Production unlinked"
          value={data.summary.production_unlinked}
        />
        <SummaryTile
          label="Sandbox linked"
          value={data.summary.sandbox_linked}
        />
        <SummaryTile
          label="Sandbox unlinked"
          value={data.summary.sandbox_unlinked}
        />
      </div>

      <ConflictsTable rows={data.conflicts ?? []} />
      <TenantSyncPanel
        conflictOrgIds={
          new Set((data.conflicts ?? []).map((conflict) => conflict.org_id))
        }
      />

      <CoverageTable
        filename="hubspot-unlinked-organizations.csv"
        rows={data.unlinked}
        title="Missing HubSpot links"
      />
      <CoverageTable
        filename="hubspot-exact-matches.csv"
        rows={data.exact_unlinked_matches}
        title="Exact matches available"
      />
      <CoverageTable
        filename="hubspot-sandbox-parent-inheritance.csv"
        rows={data.sandbox_can_inherit_from_parent}
        title="Sandbox links available from parent"
      />
    </div>
  )
}

export default OrganizationHubspotCoverage
